// Cockpit revamp — left rail.
//
// Stacked Identity / Audience / AI-context cards. (LT-539: the Sequence
// card was removed — sequences are system-managed and hidden from the UI.)
import React from 'react';

import { Card } from '../../../ui';
import type { Campaign } from '../../../types/campaign';
import CampaignAiContextCard from './CampaignAiContextCard';

interface CampaignLeftRailProps {
  campaign: Campaign;
  isPreLaunch: boolean;
  activeEnrolments?: number | null;
  onCampaignChange: (next: Campaign) => void;
  onAuditChanged: () => void;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function audienceSummary(campaign: Campaign): { label: string; sub: string } {
  if (campaign.contact_list_id) {
    return {
      label: 'Contact list',
      sub: campaign.contact_list_snapshot_id
        ? 'Snapshotted at launch'
        : 'Will snapshot at launch',
    };
  }
  if (campaign.audience_filter && Object.keys(campaign.audience_filter).length > 0) {
    return {
      label: 'Filter',
      sub: 'Dynamic audience filter',
    };
  }
  return { label: 'Not set', sub: 'Pick a contact list or filter' };
}

const Eyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">
    {children}
  </div>
);

const CampaignLeftRail: React.FC<CampaignLeftRailProps> = ({
  campaign,
  isPreLaunch,
  activeEnrolments,
  onCampaignChange,
  onAuditChanged,
}) => {
  const audience = audienceSummary(campaign);

  return (
    <div className="flex flex-col gap-3">
      <Card padding="tight">
        <Eyebrow>Identity</Eyebrow>
        <dl className="text-xs text-slate-200 leading-relaxed">
          <div className="flex justify-between gap-2">
            <dt className="text-slate-500">Owner</dt>
            <dd className="text-right truncate" title={campaign.owner_name || campaign.owner_user_id || undefined}>
              {campaign.owner_name
                || (campaign.owner_user_id ? campaign.owner_user_id.slice(0, 8) : '—')}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-slate-500">
              {isPreLaunch ? 'Created' : 'Launched'}
            </dt>
            <dd>{formatDate(isPreLaunch ? campaign.created_at : campaign.launched_at)}</dd>
          </div>
          {!isPreLaunch && campaign.enrolment_closes_at ? (
            <div className="flex justify-between">
              <dt className="text-slate-500">Enrolment closes</dt>
              <dd>{formatDate(campaign.enrolment_closes_at)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between">
            <dt className="text-slate-500">Status</dt>
            <dd className="font-mono text-slate-200">{campaign.status}</dd>
          </div>
        </dl>
      </Card>

      <Card padding="tight">
        <Eyebrow>Audience</Eyebrow>
        <div className="text-xs">
          <div className="text-slate-200 font-medium">{audience.label}</div>
          <div className="text-slate-500 mt-0.5">{audience.sub}</div>
        </div>
      </Card>

      <CampaignAiContextCard
        campaign={campaign}
        isPreLaunch={isPreLaunch}
        activeEnrolments={activeEnrolments}
        onCampaignChange={onCampaignChange}
        onAuditChanged={onAuditChanged}
      />
    </div>
  );
};

export default CampaignLeftRail;
